import { scopeProjectRef } from "@cleo/client-runtime/environment";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@cleo/client-runtime/state/runtime";
import type { ScopedThreadRef } from "@cleo/contracts";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";

import { openCommandPalette } from "~/commandPaletteBus";
import { DraftId, useComposerDraftSessionStore } from "~/composerDraftStore";
import { showContextMenuFallback } from "~/contextMenuFallback";
import { useNewThreadHandler } from "~/hooks/useHandleNewThread";
import { useThreadActionMenu } from "~/hooks/useThreadActionMenu";
import { useClientSettings } from "~/hooks/useSettings";
import { useThreadActions } from "~/hooks/useThreadActions";
import {
  localRailFolderId,
  localRailNavigation,
  type LocalRailThreadInput,
  type RailGroupRow,
  type RailSessionRow,
} from "~/neucleos/railNavigationModel";
import { localSessionNodeId } from "~/neucleos/localView";
import { sessionFace } from "~/neucleos/sessionFace";
import { createRootSessionLauncher, waitForCreatedRootShell } from "~/rootSessionBirth";
import { type RootSessionStatus, moreUrgentRootSessionStatus } from "~/rootSessionStrip";
import { useRootSessionThreadInputs } from "~/rootSessionStripContainer";
import { SESSION_RAIL_ACCENT_READ } from "~/sessionAccent";
import { useProjects, useThreadShells } from "~/state/entities";
import { threadEnvironment } from "~/state/threads";
import { useAtomCommand } from "~/state/use-atom-command";
import { parseSessionSessionLeafPath } from "~/worldViewRoute";
import { renderRailGroup, type RailGroupOrgans, type RailRowOrgans } from "./RailNavigationRows";
import { sessionHoverFacts } from "./railHoverCard.logic";
import { conversationDragSourceProps } from "./chat/conversationDrag";
import { PlusIcon } from "lucide-react";
import { toastManager } from "./ui/toast";
import { cn } from "~/lib/utils";

/**
 * RM-CAN-14: LOCAL'S ORGANS for the one Room Rail. Local fills the navigation
 * model from its own truth — project folders and ungoverned threads, in
 * Local's own ids (`environmentId:threadId`) — and hands the one row renderer
 * (`RailNavigationRows`) ungoverned verbs: open a conversation, start one in a
 * folder (the empty folder's way in), name a child of one, and the thread's
 * own menu (Pin, Settle, Snooze, Rename, Archive, Delete), its pin and its
 * archive. Rows keep the seats and the order Local's strip gave them: pinned
 * first, and a snoozed or settled thread parked off the rail unless addressed.
 * It never wears a governed record: no `world-ready` state, no R_/S_ id, no
 * retirement, no World pin preference. Those seats are `null` here and the
 * renderer does not draw them.
 */
const FOLDS_KEY = "cleo:room-rail:local-folds";
const NO_FOLDS: Readonly<Record<string, boolean>> = {};

function readFolds(): Readonly<Record<string, boolean>> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(FOLDS_KEY) ?? "{}");
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return NO_FOLDS;
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
      ),
    );
  } catch {
    return NO_FOLDS;
  }
}

export function LocalRoomRailRows({
  wide,
  onExpand,
  scrollFrame,
}: {
  readonly wide: boolean;
  /** Widen the dock: a verb that needs room to be answered (the child's name) asks for it. */
  readonly onExpand?: (() => void) | undefined;
  /** The dock's scroll frame: the Folders row stands above it, the folders scroll in it. */
  readonly scrollFrame?: ((rows: ReactNode) => ReactNode) | undefined;
}): ReactElement {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const leaf = parseSessionSessionLeafPath(pathname);
  const shells = useThreadShells();
  const projects = useProjects();
  const handleNewThread = useNewThreadHandler();
  const addressedDraft = useComposerDraftSessionStore((store) =>
    leaf?.kind === "draft" ? store.getDraftSession(DraftId.make(leaf.draftId)) : null,
  );
  // The addressed conversation: a thread leaf, or the thread an unstarted
  // draft already holds (Local births it before the first message).
  const activeId =
    leaf?.kind === "thread"
      ? localSessionNodeId(leaf.environmentId, leaf.threadId)
      : addressedDraft
        ? localSessionNodeId(addressedDraft.environmentId, addressedDraft.threadId)
        : null;

  const shellByRowId = useMemo(
    () =>
      new Map(shells.map((shell) => [localSessionNodeId(shell.environmentId, shell.id), shell])),
    [shells],
  );
  // The same per-thread inputs the Local strip read: status (seen-cursor,
  // dismissed failures) and the EFFECTIVE lifecycle (a snooze outranks a pin,
  // auto-settle, per-environment capability), so a row's ring, its place and
  // whether it keeps a seat mean what they meant there.
  const threadInputs = useRootSessionThreadInputs();
  const inputById = useMemo(
    () =>
      new Map(
        threadInputs.map((input) => [
          localSessionNodeId(input.environmentId, input.threadId),
          input,
        ]),
      ),
    [threadInputs],
  );
  const model = useMemo(() => {
    const threads: ReadonlyArray<LocalRailThreadInput> = shells.map((shell) => {
      const lifecycle = inputById.get(localSessionNodeId(shell.environmentId, shell.id));
      return {
        environmentId: shell.environmentId,
        threadId: shell.id,
        projectId: shell.projectId,
        worldKey: shell.worldKey ?? null,
        parentThreadId: shell.parentThreadId ?? null,
        title: shell.title ?? null,
        archivedAt: shell.archivedAt ?? null,
        pinnedAt: lifecycle?.pinnedAt ?? null,
        snoozedUntil: lifecycle?.snoozedUntil ?? null,
        settledAt: lifecycle?.settledAt ?? null,
      };
    });
    return localRailNavigation({
      projects: projects.map((project) => ({
        environmentId: project.environmentId,
        projectId: project.id,
        title: project.title,
        createdAt: project.createdAt,
      })),
      threads,
      activeId,
    });
  }, [activeId, inputById, projects, shells]);

  const status = useCallback(
    (id: string): RootSessionStatus | null => inputById.get(id)?.status ?? null,
    [inputById],
  );
  const descendantStatus = (row: RailSessionRow): RootSessionStatus | null =>
    row.children.reduce<RootSessionStatus | null>(
      (rollup, child) =>
        moreUrgentRootSessionStatus(
          moreUrgentRootSessionStatus(rollup, status(child.id)),
          descendantStatus(child),
        ),
      null,
    );
  const rowById = useMemo(() => {
    const byId = new Map<string, RailSessionRow>();
    const index = (row: RailSessionRow): void => {
      byId.set(row.id, row);
      row.children.forEach(index);
    };
    for (const group of model.groups) group.sessions.forEach(index);
    return byId;
  }, [model]);

  const [folds, setFolds] = useState<Readonly<Record<string, boolean>>>(() =>
    typeof window === "undefined" ? NO_FOLDS : readFolds(),
  );
  const saveFold = (key: string, open: boolean) => {
    const next = { ...folds, [key]: open };
    setFolds(next);
    try {
      window.localStorage.setItem(FOLDS_KEY, JSON.stringify(next));
    } catch {
      // Storage refusal never breaks the rail; the fold holds for this sitting.
    }
  };

  const address = (row: RailSessionRow) => {
    if (row.thread === null || row.id === activeId) return;
    void navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId: row.thread.environmentId, threadId: row.thread.threadId },
    } as never);
  };

  // Rename commits through the same updateMetadata door every thread rename uses.
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, {
    reportFailure: false,
  });
  const [renaming, setRenaming] = useState<{
    readonly id: string;
    readonly threadRef: ScopedThreadRef;
    readonly title: string;
  } | null>(null);
  const commitRename = () => {
    if (renaming === null) return;
    const title = renaming.title.trim();
    setRenaming(null);
    if (title === "") {
      toastManager.add({ type: "warning", title: "Session title cannot be empty" });
      return;
    }
    void updateThreadMetadata({
      environmentId: renaming.threadRef.environmentId,
      input: { threadId: renaming.threadRef.threadId, title },
    }).then((result) => {
      if (result._tag !== "Failure" || isAtomCommandInterrupted(result)) return;
      const error = squashAtomCommandFailure(result);
      toastManager.add({
        type: "error",
        title: "Failed to rename session",
        description: error instanceof Error ? error.message : "An error occurred.",
      });
    });
  };
  // Pin and archive are the thread's own doors (useThreadActions): the pin the
  // strip ordered by, and the leaving verb the sidebar row carried.
  const { archiveThread, pinThread, unpinThread } = useThreadActions();
  // The confirmThreadArchive setting ("Require a second click on the inline archive
  // action before a thread is archived") governs this rail's archive: the row's seat
  // AND the menu's Archive. With it on, the first gesture only asks — the row's seat
  // reads "Confirm" — and the second archives; the pointer leaving that row, or
  // another row being asked, withdraws the question. Its old consumer was the
  // sidebar row (deleted 180eaf42); until 20 September's second review nothing read it.
  const confirmThreadArchive = useClientSettings((settings) => settings.confirmThreadArchive);
  const [confirmingArchiveId, setConfirmingArchiveId] = useState<string | null>(null);
  const archiveNow = (row: RailSessionRow) => {
    if (row.thread === null) return;
    void archiveThread(row.thread as ScopedThreadRef).then((result) => {
      if (result._tag !== "Failure" || isAtomCommandInterrupted(result)) return;
      const error = squashAtomCommandFailure(result);
      toastManager.add({
        type: "error",
        title: "The conversation was not archived",
        description:
          error instanceof Error ? error.message : "Stop its running turn first, then archive.",
      });
    });
  };
  const archive = (row: RailSessionRow) => {
    if (confirmThreadArchive && confirmingArchiveId !== row.id) {
      setConfirmingArchiveId(row.id);
      return;
    }
    setConfirmingArchiveId(null);
    archiveNow(row);
  };
  const { openMenu } = useThreadActionMenu({
    threadRef: null,
    projectCwd: null,
    changeRequestState: null,
    onStartRename: () => {},
    accent: SESSION_RAIL_ACCENT_READ,
  });

  // New child: one named ask under the parent, then exactly one durable
  // `thread.create` naming that parent — the door the old strip's "+New Child"
  // used (rootSessionBirth), so a double Enter is a refusal, never a twin.
  const createThread = useAtomCommand(threadEnvironment.create);
  const childEnvironment = useRef<string | null>(null);
  const launcher = useMemo(
    () =>
      createRootSessionLauncher({
        dispatch: (input) => {
          const environmentId = childEnvironment.current;
          if (environmentId === null) return Promise.resolve({ ok: false });
          return createThread({ environmentId: environmentId as never, input }).then((result) => ({
            ok: result._tag !== "Failure",
          }));
        },
      }),
    [createThread],
  );
  const [childAsk, setChildAsk] = useState<{
    readonly parentId: string;
    readonly title: string;
    readonly busy: boolean;
  } | null>(null);
  const submitChild = (row: RailSessionRow) => {
    const parent = shellByRowId.get(row.id);
    if (childAsk === null || childAsk.busy || parent === undefined) return;
    if (childAsk.title.trim() === "") return;
    setChildAsk({ ...childAsk, busy: true });
    childEnvironment.current = parent.environmentId;
    void (async () => {
      const outcome = await launcher.launch({
        mode: "local",
        activeWorldKey: null,
        activeSessionId: null,
        projectId: parent.projectId,
        ask: childAsk.title,
        now: new Date().toISOString(),
        modelSelection: parent.modelSelection,
        ...(parent.localWorkPolicy ? { localWorkPolicy: parent.localWorkPolicy } : {}),
        runtimeMode: parent.runtimeMode,
        interactionMode: parent.interactionMode,
        branch: null,
        worktreePath: null,
        parentThreadId: parent.id,
      });
      // An accepted child is addressed once its shell arrives; a failed
      // dispatch is checked against the stream first, because an
      // acknowledgement can be lost after the record was made.
      const threadId = outcome._tag === "Refused" ? undefined : outcome.threadId;
      const arrived =
        threadId !== undefined && (await waitForCreatedRootShell(parent.environmentId, threadId));
      if (arrived && threadId !== undefined) {
        setChildAsk(null);
        await navigate({
          to: "/$environmentId/$threadId",
          params: { environmentId: parent.environmentId, threadId },
        } as never);
        return;
      }
      setChildAsk((current) => (current === null ? current : { ...current, busy: false }));
      toastManager.add({
        type: outcome._tag === "Launched" ? "info" : "error",
        title: outcome._tag === "Launched" ? "The child was made" : "The child was not made",
        description:
          outcome._tag === "Launched"
            ? "It will appear under its parent when the Desk hears of it."
            : "Nothing was created. Press Enter to try again.",
      });
    })();
  };

  const dragSource = (row: RailSessionRow, children: ReactNode): ReactNode => {
    const thread = row.thread as ScopedThreadRef | null;
    const props = conversationDragSourceProps(
      thread === null ? null : { threadId: String(thread.threadId), title: row.label },
    );
    return "draggable" in props ? (
      <div key={row.id} {...props}>
        {children}
      </div>
    ) : (
      children
    );
  };
  const rowOrgans: RailRowOrgans = {
    wide,
    scopedGroup: false,
    addressedId: activeId,
    activePin: false,
    searching: false,
    filterKey: "all",
    foldChoices: folds,
    onFoldRow: saveFold,
    status,
    descendantStatus: (id) => {
      const row = rowById.get(id);
      return row === undefined ? null : descendantStatus(row);
    },
    groupLabel: (groupId) => model.groups.find((group) => group.id === groupId)?.label ?? null,
    address,
    openMenu: (row, position) => {
      if (row.thread === null) return;
      const threadRef = row.thread as ScopedThreadRef;
      // Local keeps every thread word: Pin, Settle, Snooze, Rename, Delete.
      // ("session" is a World's noun and hides Settle and Snooze.)
      openMenu(position, {
        threadRef,
        // The strip's "+New Child", at any dock width: the narrow dock has no room for
        // the child's name, so it widens first and the ask opens under the parent.
        onNewChild: () => {
          if (!wide) onExpand?.();
          setChildAsk({ parentId: row.id, title: "", busy: false });
        },
        onStartRename: () => setRenaming({ id: row.id, threadRef, title: row.label }),
        onArchive: () => archive(row),
      });
    },
    threadRefOf: (row) => row.thread as ScopedThreadRef | null,
    hoverFacts: (row, meta) => {
      const shell = shellByRowId.get(row.id);
      return sessionHoverFacts({
        label: row.label,
        // A Local row's group is a folder, not a room.
        meta: meta.map((line) => (line?.startsWith("Room: ") ? `Folder: ${line.slice(6)}` : line)),
        executions: shell === undefined ? [] : [shell],
      });
    },
    hoverDisabled: (row) => renaming?.id === row.id || childAsk?.parentId === row.id,
    // Cleo Can 53: a Local row has no sort, so it is a native drag source for
    // its conversation; dropped on a composer it becomes a reference chip.
    wrapRow: (row, children) => dragSource(row, children),
    wrapPinnedRow: (row, children) => dragSource(row, children),
    belowRow: () => null,
    // Local's pin is the THREAD's (pinnedAt), the one its rows are ordered by.
    pins: {
      pinned: (id) => inputById.get(id)?.pinnedAt != null,
      toggle: (id) => {
        const shell = shellByRowId.get(id);
        if (shell === undefined) return;
        const threadRef = { environmentId: shell.environmentId, threadId: shell.id };
        void (inputById.get(id)?.pinnedAt != null ? unpinThread(threadRef) : pinThread(threadRef));
      },
    },
    // Governed-only seats: retirement, the close ceremony, the persisted manual
    // order and the file attachment door are a World's; the renderer draws none.
    retire: null,
    hoverSeats: "floating",
    archive: {
      confirmingId: confirmingArchiveId,
      onPress: archive,
      onLeave: () => setConfirmingArchiveId(null),
    },
    requestClose: null,
    fileDrop: null,
    compactMetrics: null,
    rename:
      renaming === null
        ? null
        : {
            id: renaming.id,
            title: renaming.title,
            onChange: (title) => setRenaming((prev) => (prev === null ? prev : { ...prev, title })),
            onCommit: commitRename,
            onCancel: () => setRenaming(null),
          },
    childAsk: {
      parentId: childAsk?.parentId ?? null,
      title: childAsk?.title ?? "",
      busy: childAsk?.busy ?? false,
      onOpen: (row) => setChildAsk({ parentId: row.id, title: "", busy: false }),
      onChange: (title) => setChildAsk((prev) => (prev === null ? prev : { ...prev, title })),
      onSubmit: submitChild,
      onCancel: () => setChildAsk(null),
    },
  };

  const groupStatuses = (group: RailGroupRow): ReadonlyArray<RootSessionStatus | null> => {
    const statuses: Array<RootSessionStatus | null> = [];
    const visit = (row: RailSessionRow): void => {
      statuses.push(status(row.id));
      row.children.forEach(visit);
    };
    group.sessions.forEach(visit);
    return statuses;
  };
  const newSessionIn = (group: RailGroupRow) => {
    const project = projects.find(
      (candidate) => localRailFolderId(candidate.environmentId, candidate.id) === group.id,
    );
    if (project === undefined) return;
    void handleNewThread(scopeProjectRef(project.environmentId, project.id));
  };
  // The strip's "+New session", at any dock width: in the addressed conversation's
  // folder; with nothing addressed, in the only folder there is; with several, the
  // person is asked which (Search & commands, "new thread in").
  const newSession = () => {
    const addressedShell = activeId === null ? undefined : shellByRowId.get(activeId);
    const project =
      addressedShell !== undefined
        ? projects.find(
            (candidate) =>
              candidate.environmentId === addressedShell.environmentId &&
              candidate.id === addressedShell.projectId,
          )
        : projects.length === 1
          ? projects[0]
          : undefined;
    if (project === undefined) {
      openCommandPalette({ open: projects.length === 0 ? "add-project" : "new-thread-in" });
      return;
    }
    void handleNewThread(scopeProjectRef(project.environmentId, project.id));
  };
  // A folder's own menu: its one verb, reachable by right-click and by the keyboard in
  // the narrow dock too, where the header has no room for a "+".
  const openFolderMenu = (group: RailGroupRow, position: { x: number; y: number }) => {
    void showContextMenuFallback(
      [{ id: "folder-new-session", label: "New session in this folder" }],
      position,
      { accent: SESSION_RAIL_ACCENT_READ },
    ).then((action) => {
      if (action === "folder-new-session") newSessionIn(group);
    });
  };
  const groupOrgans: RailGroupOrgans = {
    wide,
    open: (group) => folds[`folder:${group.id}`] ?? true,
    header: (group, open) => {
      const sessionStatuses = groupStatuses(group);
      return {
        roomId: group.id,
        label: group.label,
        standing: false,
        face: sessionFace(
          sessionStatuses.reduce<RootSessionStatus | null>(moreUrgentRootSessionStatus, null),
        ),
        sessionStatuses,
        open,
        current: group.sessions.some((row) => row.addressed),
        wide,
        onToggle: () => saveFold(`folder:${group.id}`, !open),
        // A folder has no governed room archive; its verb is New session, on its "+"
        // seat when wide and in its own menu at any width.
        onMenu: (position) => openFolderMenu(group, position),
        onNewSession: () => newSessionIn(group),
      };
    },
    wrapGroup: (_group, children) => children,
    foot: () => null,
    emptyBody: null,
  };

  const rows = (
    <div className="ncl-reactbits flex w-full flex-none flex-col gap-4" data-room-rail-list="">
      {model.groups.map((group) => renderRailGroup(groupOrgans, rowOrgans, group))}
      {model.groups.length === 0 ? (
        <p className={wide ? "px-2 py-4 text-xs text-muted-foreground" : "sr-only"}>
          No projects yet
        </p>
      ) : null}
    </div>
  );
  return (
    <div
      className={cn(
        "flex w-full flex-col",
        scrollFrame ? "min-h-0 flex-1" : "flex-none",
        wide ? "" : "items-center",
      )}
      data-room-rail=""
      data-room-rail-authority="local"
    >
      <div
        className={wide ? "flex items-center gap-1 px-1 pb-7" : "flex flex-col items-center pb-7"}
        data-room-rail-header=""
      >
        {wide ? (
          <span className="flex-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Folders
          </span>
        ) : null}
        <button
          aria-label="New session"
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
          data-deck-plus=""
          data-room-rail-new-session="local"
          onClick={newSession}
          title="New session"
          type="button"
        >
          <PlusIcon className="size-3.5" />
        </button>
      </div>
      {scrollFrame ? scrollFrame(rows) : rows}
    </div>
  );
}
